/**
 * Campaign-composer slice of the English catalog.
 *
 * `POST /api/campaigns` has existed, validated and permission-gated, the whole
 * time — the CLIENT had no `create` method. The W11b prune removed 28 unused
 * wrappers and this was one of them, so the only way to get a campaign was to
 * ask the AI assistant, which the Campaigns page then described as "created
 * elsewhere". Six campaigns exist in production, the newest from 2026-07-21.
 *
 * en.ts sits against the 500-line fileSize ceiling, so new keys live in a slice
 * and are spread back into `en` there — the pattern enAds.ts documents.
 */
export const enCampaigns = {
  // ── Entry points ──────────────────────────────────────────────────────
  'campaigns.new': 'New campaign',
  'campaigns.searchPlaceholder': 'Search campaigns…',
  'campaigns.statusFiltersAria': 'Filter campaigns by status',
  'campaigns.emptyTitle': 'No campaigns yet',
  'campaigns.emptyBody':
    'A campaign is one email sent once, to everyone or to a segment. Write it here, then send it when you are ready.',
  'campaigns.emptyNoMatches': 'No matches',
  'campaigns.emptyNoMatchesBody': 'Try a different search or status filter.',

  // ── The composer ──────────────────────────────────────────────────────
  'campaigns.compose.heading': 'New campaign',
  'campaigns.compose.nameLabel': 'Internal name',
  'campaigns.compose.namePlaceholder': 'March newsletter',
  'campaigns.compose.nameHint': 'Only you see this. It names the campaign in this list.',
  'campaigns.compose.subjectLabel': 'Subject line',
  'campaigns.compose.subjectPlaceholder': 'What your customers see first',
  'campaigns.compose.contentLabel': 'Email body',
  // Double braces: the campaign sender (server emailSender/mergeTags.ts)
  // resolves `{{firstName}}` and sends a single-brace token through raw.
  'campaigns.compose.contentPlaceholder': 'Hi {{firstName}}, …',
  'campaigns.compose.contentHint':
    'Plain text or HTML. {{firstName}} and the other merge tags are filled in per customer when it sends.',
  'campaigns.compose.audienceLabel': 'Send to',
  'campaigns.compose.audienceAll': 'All customers',
  'campaigns.compose.audienceHint':
    'Choose a saved segment to narrow it. You can change this before sending.',
  'campaigns.compose.submit': 'Save as draft',
  'campaigns.compose.submitting': 'Saving…',
  'campaigns.compose.cancel': 'Cancel',
  'campaigns.compose.editHeading': 'Edit draft',
  'campaigns.compose.audienceCannotClear':
    'A saved draft cannot be switched back to all customers yet. Pick a segment, or start a new campaign for everyone.',
  'campaigns.openDraft': 'Open draft',
  'campaigns.openFailed': 'Could not open the draft. Try again.',

  // ── What happens next, said plainly ───────────────────────────────────
  'campaigns.compose.draftNote':
    'Saving creates a draft. Nothing is sent until you press Send on its row.',

  // ── Outcomes ──────────────────────────────────────────────────────────
  'campaigns.created': 'Draft saved. Preview it, then send it from its row when you are ready.',
  'campaigns.createFailed': 'Could not save the campaign.',
  'campaigns.updated': 'Draft updated.',

  // ── Send confirmation — the number before anything leaves ─────────────
  'campaigns.send.confirmCount':
    'Send “{name}” to {count} people in “{segment}”? People who opted out of email are skipped.',
  'campaigns.send.confirmCountConsented':
    'Send “{name}” to {count} people in “{segment}” who agreed to receive email?',
  'campaigns.send.confirmSegmentUnknown':
    'Send “{name}” to its saved segment? The segment and the number of people are not available right now. People who opted out of email are skipped.',
  'campaigns.send.confirmSegmentNoCount':
    'Send “{name}” to the segment “{segment}”? The number of people is not available right now. People who opted out of email are skipped.',
  'campaigns.send.confirmAllCount':
    'Send “{name}” to {count} people — all customers who agreed to receive email?',
  'campaigns.send.confirmAllNoCount':
    'Send “{name}” to all customers? The number of people is not available here. People who opted out of email are skipped.',
  'campaigns.nameRequired': 'Give the campaign a name.',
  'campaigns.subjectRequired': 'An email needs a subject line.',
  'campaigns.contentRequired': 'Write something in the body.',

  // ── Preview (read-only — nothing is sent) ─────────────────────────────
  'campaigns.preview.open': 'Preview',
  'campaigns.preview.title': 'Preview',
  'campaigns.preview.description':
    'This is the email as {name} would receive it. Nothing is sent from this view.',
  'campaigns.preview.descriptionUnnamed':
    'This is the email as a customer with no name on file would receive it. Nothing is sent from this view.',
  'campaigns.preview.recipientLabel': 'Preview as',
  'campaigns.preview.recipientNamed': 'Customer with a name',
  'campaigns.preview.recipientUnnamed': 'Customer without a name',
  'campaigns.preview.from': 'From',
  'campaigns.preview.replyTo': 'Reply to',
  'campaigns.preview.subject': 'Subject',
  'campaigns.preview.audience': 'Send to',
  'campaigns.preview.audienceAll': 'All customers',
  'campaigns.preview.noSubject': '(no subject)',
  'campaigns.preview.empty': 'This campaign has no body yet. Add one before sending.',
  'campaigns.preview.frameTitle': 'Email preview',
  'campaigns.preview.loading': 'Loading preview…',
  'campaigns.preview.failed': 'Could not load the preview. Close this panel and try again.',
  // Calendar day summary for a day whose slots have all passed. Lives here
  // rather than in en.ts, which sits at the 500-line ceiling.
  'calendar.day.postedCount': '{count} posted',
  // ── The welcome (V63): which flow greets every new person ──────────────
  'flows.welcome.badge': 'The welcome',
  'flows.welcome.badgeHint': 'Every new contact, lead or subscriber gets this one welcome — from any form, booking, store or manual add.',
  'flows.welcome.not': "Not the welcome — won't send to new sign-ups",
  'flows.welcome.notHint': 'Another flow is the welcome, so this one stays quiet for new sign-ups.',
  'flows.welcome.chosenInactive': 'Chosen as the welcome — activate it to send',
  'flows.welcome.make': 'Make this the welcome',
  'flows.welcome.made': '"{name}" is now the welcome for every new person.',
  'flows.welcome.failed': "Couldn't change the welcome. Try again.",
} as const;

// Re-exported so en.ts can compose it on its existing enCampaigns import line.
export { enTemplates } from './enTemplates';
